/**
 * Streams a POST /api/conversations/:id/messages response and surfaces both
 * the standard OpenAI-style content deltas AND the chat-backend's synthetic
 * `chat_event` SSE events (tool_call_start/done, error, limit_reached).
 *
 * The `signal` lets the caller abort the request — a browser disconnect
 * propagates all the way to the gateway.
 */

export type ChatStreamEvent =
  | { kind: 'content_delta'; text: string }
  | { kind: 'finish'; reason: string | null; tokensIn: number | null; tokensOut: number | null }
  | { kind: 'tool_call_start'; name: string; id: string }
  | { kind: 'tool_call_done'; name: string; id: string; ok: boolean }
  | { kind: 'mail_send_confirmation_required'; draft: MicrosoftMailSendDraft }
  | { kind: 'limit_reached' }
  | { kind: 'notice'; code: StreamNoticeCode }
  /** The server corrects the live text: withdrawn before a tool call, or the cleaned final answer. */
  | { kind: 'replace_text'; text: string }
  /** Model "nivio": a progress event of the orchestrator (plan, step, cost). */
  | { kind: 'orchestration'; event: unknown }
  | { kind: 'error'; message: string };

/** Recovery notes from the chat backend (`StreamNotice` in src/tools/agenticLoop.ts). */
export type StreamNoticeCode =
  | 'tools_unavailable'
  | 'tool_denied'
  | 'forced_final'
  | 'tool_calls_dropped';

const NOTICE_CODES: readonly string[] = [
  'tools_unavailable',
  'tool_denied',
  'forced_final',
  'tool_calls_dropped',
];

export type MicrosoftMailSendDraft = {
  to: string[];
  cc: string[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
};

type RawChunk = {
  choices?: Array<{
    delta?: {
      content?: string;
      role?: string;
      tool_calls?: unknown;
    };
    finish_reason?: string | null;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
  };
  chat_event?:
    | 'tool_call_start'
    | 'tool_call_done'
    | 'mail_send_confirmation_required'
    | 'limit_reached'
    | 'notice'
    | 'replace_text'
    | 'orchestration'
    | 'error';
  code?: string;
  event?: unknown;
  text?: string;
  name?: string;
  id?: string;
  ok?: boolean;
  message?: string;
  draft?: unknown;
};

export async function* streamChatPost(args: {
  url: string;
  body: unknown;
  csrfToken: string;
  signal: AbortSignal;
}): AsyncGenerator<ChatStreamEvent> {
  const res = await fetch(args.url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
      'x-csrf-token': args.csrfToken,
    },
    body: JSON.stringify(args.body),
    signal: args.signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    yield { kind: 'error', message: text || `request failed (${res.status})` };
    return;
  }

  let tokensIn: number | null = null;
  let tokensOut: number | null = null;
  let finishReason: string | null = null;
  const emittedFinish = false;

  for await (const evt of iterSseEvents(res.body)) {
    if (evt === '[DONE]') break;
    const chunk = evt as RawChunk;
    if (chunk.chat_event) {
      switch (chunk.chat_event) {
        case 'tool_call_start':
          yield { kind: 'tool_call_start', name: chunk.name ?? '', id: chunk.id ?? '' };
          break;
        case 'tool_call_done':
          yield {
            kind: 'tool_call_done',
            name: chunk.name ?? '',
            id: chunk.id ?? '',
            ok: chunk.ok ?? false,
          };
          break;
        case 'mail_send_confirmation_required': {
          const draft = parseMailSendDraft(chunk.draft);
          if (draft) yield { kind: 'mail_send_confirmation_required', draft };
          break;
        }
        case 'limit_reached':
          yield { kind: 'limit_reached' };
          break;
        case 'replace_text':
          yield { kind: 'replace_text', text: chunk.text ?? '' };
          break;
        case 'orchestration':
          if (chunk.event) yield { kind: 'orchestration', event: chunk.event };
          break;
        case 'notice':
          if (chunk.code && NOTICE_CODES.includes(chunk.code))
            yield { kind: 'notice', code: chunk.code as StreamNoticeCode };
          break;
        case 'error':
          yield { kind: 'error', message: chunk.message ?? 'unknown error' };
          break;
      }
      continue;
    }
    const delta = chunk.choices?.[0]?.delta?.content;
    if (delta) yield { kind: 'content_delta', text: delta };
    const reason = chunk.choices?.[0]?.finish_reason;
    if (reason) finishReason = reason;
    if (typeof chunk.usage?.prompt_tokens === 'number') tokensIn = chunk.usage.prompt_tokens;
    if (typeof chunk.usage?.completion_tokens === 'number')
      tokensOut = chunk.usage.completion_tokens;
  }
  if (!emittedFinish) {
    yield { kind: 'finish', reason: finishReason, tokensIn, tokensOut };
  }
}

function parseMailSendDraft(value: unknown): MicrosoftMailSendDraft | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const to = stringArray(raw.to);
  const cc = stringArray(raw.cc);
  const subject = typeof raw.subject === 'string' ? raw.subject : '';
  const bodyText = typeof raw.bodyText === 'string' ? raw.bodyText : '';
  const bodyHtml = typeof raw.bodyHtml === 'string' ? raw.bodyHtml : '';
  if (
    to.length === 0 ||
    subject.trim().length === 0 ||
    (bodyText.trim().length === 0 && bodyHtml.trim().length === 0)
  ) {
    return null;
  }
  return {
    to,
    cc,
    subject,
    ...(bodyText.trim().length > 0 ? { bodyText } : {}),
    ...(bodyHtml.trim().length > 0 ? { bodyHtml } : {}),
  };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

async function* iterSseEvents(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<unknown | '[DONE]'> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep = buf.indexOf('\n\n');
    while (sep !== -1) {
      const block = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const payload = extractDataLines(block);
      if (payload !== null) {
        if (payload === '[DONE]') {
          yield '[DONE]';
        } else {
          try {
            yield JSON.parse(payload);
          } catch {
            // ignore malformed
          }
        }
      }
      sep = buf.indexOf('\n\n');
    }
  }
}

function extractDataLines(block: string): string | null {
  const lines = block.split('\n');
  const dataParts: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('data:')) {
      dataParts.push(line.slice(line[5] === ' ' ? 6 : 5));
    }
  }
  if (dataParts.length === 0) return null;
  return dataParts.join('\n');
}

export function readCsrfTokenFromCookie(): string {
  return (
    (__PRODUCT_PROFILE__ === 'light'
      ? readCookie('__Host-ncl_csrf')
      : (readCookie('__Host-nc_csrf') ?? readCookie('nc_csrf'))) ?? ''
  );
}

function readCookie(name: string): string | null {
  const target = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) return decodeURIComponent(trimmed.slice(target.length));
  }
  return null;
}
